import { useRef, useEffect, useState } from 'react';

const steps = [
  {
    number: '01',
    icon: 'ri-phone-line',
    title: 'Contato Inicial',
    desc: 'Entre em contato pelo WhatsApp, e-mail ou telefone. Apresente sua situação e nossa equipe responderá prontamente para agendar seu atendimento.',
  },
  {
    number: '02',
    icon: 'ri-search-eye-line',
    title: 'Análise do Caso',
    desc: 'Realizamos um estudo aprofundado da sua situação jurídica, identificando riscos, oportunidades e o melhor caminho a seguir para sua demanda.',
  },
  {
    number: '03',
    icon: 'ri-file-list-3-line',
    title: 'Estratégia Jurídica',
    desc: 'Desenvolvemos uma estratégia jurídica personalizada, com clareza sobre prazos, procedimentos e as melhores alternativas para seu caso.',
  },
  {
    number: '04',
    icon: 'ri-check-double-line',
    title: 'Acompanhamento Completo',
    desc: 'Acompanhamos todo o processo, mantendo você informado em cada etapa, com transparência, dedicação e comprometimento até a resolução.',
  },
];

export default function HowWeWork() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="como-atuamos" ref={ref} className="py-24 bg-[#F4F3EF]">
      <div className="max-w-7xl mx-auto px-4 md:px-10">
        {/* Header */}
        <div
          className={`text-center mb-16 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="flex items-center justify-center mb-4">
            <div className="h-px w-10 bg-[#C8B07A] mr-4"></div>
            <span className="text-[#C8B07A] text-xs font-semibold tracking-[0.3em] uppercase">Metodologia</span>
            <div className="h-px w-10 bg-[#C8B07A] ml-4"></div>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-[#1F2A44] font-display mb-4">
            Como Atuamos
          </h2>
          <p className="text-[#1A1A1A]/55 max-w-xl mx-auto text-sm leading-relaxed">
            Nosso fluxo de atendimento foi desenvolvido para garantir eficiência, clareza e os melhores resultados para cada cliente.
          </p>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 relative">
          {steps.map((step, index) => (
            <div
              key={step.number}
              className={`relative z-10 transition-all duration-700 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${index * 120}ms` }}
            >
              <div className="bg-white rounded-lg p-6 h-full border border-[#E5E5E5] hover:border-[#C8B07A]/40 transition-all duration-300 group relative overflow-hidden">
                {/* Number - large, right side, vertically centered */}
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-8xl font-bold text-[#E5E5E5] font-display leading-none select-none z-0 group-hover:text-[#C8B07A]/20 transition-colors duration-300">
                  {step.number}
                </span>
                {/* Content */}
                <div className="relative z-10 pr-12">
                  <h3 className="font-bold text-[#1F2A44] text-base mb-3 group-hover:text-[#C8B07A] transition-colors duration-300">
                    {step.title}
                  </h3>
                  <p className="text-[#1A1A1A]/55 text-xs leading-relaxed">
                    {step.desc}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Banner */}
        <div
          className={`mt-14 bg-[#1F2A44] rounded-lg p-8 flex flex-col md:flex-row items-center justify-between gap-6 transition-all duration-700 delay-500 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div>
            <h4 className="text-white font-bold text-xl mb-2">Pronto para iniciar?</h4>
            <p className="text-white/55 text-sm">Entre em contato agora e agende sua consulta inicial.</p>
          </div>
          <a
            href="https://wa.me/5567999210516"
            target="_blank"
            rel="nofollow noreferrer"
            className="inline-flex items-center gap-2 bg-[#C8B07A] hover:bg-[#D6C18F] text-[#1F2A44] px-7 py-3.5 rounded-md font-bold text-sm transition-all duration-300 cursor-pointer whitespace-nowrap hover:scale-105 flex-shrink-0"
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-whatsapp-line text-xl"></i>
            </span>
            Falar pelo WhatsApp
          </a>
        </div>
      </div>
    </section>
  );
}
